import { type FC, useEffect, useRef, useState } from "react";
import { cn } from "../../../util/classNameConstructor";
import Icon from "../../core/icon/Icon";
import type { IconName } from "../../core/icon/names";

const useChangeTransition = (value?: string | number) => {
  const initial = value !== undefined ? " " : "";
  const currentNumber = useRef<number | string>(initial);
  const keepNumber = useRef(value || initial);
  const [scale, setScale] = useState(1);
  const stagger = 30;
  const duration = 100;
  useEffect(() => {
    if (value === currentNumber.current) return undefined;

    // keep the value in case it changes while the animation is running
    keepNumber.current = value || initial;

    // there should be no delay on first render and it must me a number
    const delay =
      +((!!currentNumber.current && keepNumber.current?.toString()) ?? 0) + 1;

    const scaleDown = setTimeout(() => setScale(0.5), stagger * delay);

    const scaleUp = setTimeout(
      () => {
        setScale(1);
        currentNumber.current = keepNumber.current;
      },
      duration + stagger * delay,
    );

    return () => {
      clearTimeout(scaleDown);
      clearTimeout(scaleUp);
    };
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const style = {
    transform: `scale(${scale})`,
    transition: `transform ${duration}ms`,
    transformOrigin: "right",
  };
  return [style, currentNumber.current] as const;
};
type colors =
  | "neutral"
  | "blue"
  | "orange"
  | "teal"
  | "yellow"
  | "green"
  | "purple"
  | "red"
  | "brown"
  | "rose";
type tones =
  | "1000"
  | "900"
  | "800"
  | "700"
  | "600"
  | "500"
  | "400"
  | "300"
  | "200"
  | "100";

export interface IWithNumberTrueProps {
  selectIcon?: IconName;
  number?: string | number;
  maxNumber?: number;
  color?: `${colors}-${tones}` | "transparent";
  animate?: boolean;
  className?: string;
}

const IconContainer: FC<IWithNumberTrueProps> = ({
  selectIcon,
  number,
  maxNumber,
  animate = true,
  color = "neutral-500",
  className,
}) => {
  const [style, currentNumber] = useChangeTransition(number);
  return (
    <div
      style={{
        backgroundColor:
          color === "transparent"
            ? "transparent"
            : `var(--palette-${color}, var(--palette-neutral-500))`,
      }}
      className={cn(
        "relative flex h-7 min-w-[1.75rem] shrink-0 flex-row items-center justify-between gap-1 rounded-lg px-1.5",
        selectIcon && currentNumber ? "justify-between" : "justify-center",
        !currentNumber && "aspect-square",
        className,
      )}
    >
      {selectIcon ? (
        <div className="flex">
          <Icon name={selectIcon} />
        </div>
      ) : null}
      {currentNumber && (
        <div
          className="text-text-default text-md-de body-md-bold relative text-right tabular-nums leading-none"
          style={{
            minWidth: `${maxNumber?.toString().length || 1}ch`,
            ...(animate ? style : {}),
          }}
        >
          {animate ? currentNumber : number}
        </div>
      )}
    </div>
  );
};

export default IconContainer;
